import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Copy,
  Filter,
  Loader2,
  Search,
  Sparkles,
  Tag,
  UserPlus,
  Users,
} from 'lucide-react';
import MainLayout from '../../../../components/layout/MainLayout';
import { getBackofficeSidebarItems } from '../../shell/backofficeSidebar';
import { useAuth } from '../../../../context/AuthContext';
import { PaginatedListLoading } from '../../../../components/ui/PaginatedListLoading';
import { useCurrency } from '../../../../context/CurrencyContext';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3001';

const inputClass =
  'h-11 w-full rounded-xl border border-slate-200 bg-white px-3.5 text-sm text-slate-900 shadow-sm outline-none transition ring-brand-500/20 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/30';
const labelClass = 'mb-1.5 block text-sm font-medium text-slate-800';
const btnPrimary =
  'inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-brand-600 px-5 py-2.5 text-sm font-semibold text-white shadow-md shadow-brand-600/20 transition hover:bg-brand-500 disabled:opacity-50';
const btnSecondary =
  'inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-800 shadow-sm transition hover:bg-slate-50 disabled:opacity-50';
const btnGhost =
  'inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-semibold text-brand-700 transition hover:bg-brand-50';

type PromotionRow = {
  id: string;
  code: string;
  description: string | null;
  discountType: 'PERCENT' | 'FIXED_USD';
  fixedDiscountUsd: string | null;
  percentOff: string | null;
  restrictedCustomerId: string | null;
  minLifetimeSpendUsd: string | null;
  minOrderSubtotalUsd: string | null;
  maxRedemptionsTotal: number | null;
  maxRedemptionsPerCustomer: number | null;
  expiresAt: string | null;
  isActive: boolean;
  restrictedCustomer: { id: string; email: string } | null;
  _count?: { orders: number };
};

type SegmentCustomer = {
  id: string;
  email: string;
  createdAt: string;
  fullName: string | null;
  phone: string | null;
  country: string | null;
  city: string | null;
  periodSpendUsd: number | null;
  periodOrderCount: number | null;
  lifetimeSpendUsd: number;
  lifetimeOrderCount: number;
};

type SegmentResponse = {
  items: SegmentCustomer[];
  total: number;
  page: number;
  pageSize: number;
  sort: string;
  mode: 'period' | 'directory';
  purchaseFrom: string | null;
  purchaseTo: string | null;
};

function defaultPurchaseTo() {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
}

function defaultPurchaseFrom() {
  const d = new Date();
  d.setDate(1);
  d.setHours(0, 0, 0, 0);
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
}

export default function WorkspacePromotionsPage() {
  const { t } = useTranslation();
  const { authorizedFetch } = useAuth();
  const { formatPriceFrom } = useCurrency();
  const formRef = useRef<HTMLDivElement>(null);
  const globalAuthorizedFetch = useCallback(
    (input: RequestInfo | URL, init: RequestInit = {}) => {
      const headers = new Headers(init.headers || {});
      // Promotions workspace is intentionally global (not department-scoped).
      headers.set('x-department-slug', '');
      return authorizedFetch(input, { ...init, headers });
    },
    [authorizedFetch],
  );

  const [rows, setRows] = useState<PromotionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [formOpen, setFormOpen] = useState(false);

  const [segLoading, setSegLoading] = useState(false);
  const [segError, setSegError] = useState<string | null>(null);
  const [segment, setSegment] = useState<SegmentResponse | null>(null);

  const [q, setQ] = useState('');
  const [phone, setPhone] = useState('');
  const [country, setCountry] = useState('');
  const [city, setCity] = useState('');
  const [purchaseFrom, setPurchaseFrom] = useState(defaultPurchaseFrom);
  const [purchaseTo, setPurchaseTo] = useState(defaultPurchaseTo);
  const [usePurchaseWindow, setUsePurchaseWindow] = useState(true);
  const [minSpendUsd, setMinSpendUsd] = useState('');
  const [maxSpendUsd, setMaxSpendUsd] = useState('');
  const [excludeIds, setExcludeIds] = useState('');
  const [sort, setSort] = useState('period_spend_desc');
  const [page, setPage] = useState(1);
  /** Bumps when user clicks "Run search" or reset so we refetch even if page stays 1. */
  const [searchNonce, setSearchNonce] = useState(0);
  /** No segment API calls until the user runs a search at least once. */
  const [segmentSearched, setSegmentSearched] = useState(false);
  const [targetingExpanded, setTargetingExpanded] = useState(false);

  const [code, setCode] = useState('');
  const [description, setDescription] = useState('');
  const [discountType, setDiscountType] = useState<'PERCENT' | 'FIXED_USD'>('PERCENT');
  const [percentOff, setPercentOff] = useState('10');
  const [fixedDiscountUsd, setFixedDiscountUsd] = useState('5');
  const [restrictedCustomerEmail, setRestrictedCustomerEmail] = useState('');
  const [minLifetimeSpendUsd, setMinLifetimeSpendUsd] = useState('');
  const [minOrderSubtotalUsd, setMinOrderSubtotalUsd] = useState('');
  const [maxRedemptionsTotal, setMaxRedemptionsTotal] = useState('');
  const [maxRedemptionsPerCustomer, setMaxRedemptionsPerCustomer] = useState('');
  const [expiresAt, setExpiresAt] = useState('');

  const loadPromotions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await globalAuthorizedFetch(`${API_BASE}/promotions`);
      if (!res.ok) throw new Error('load');
      setRows((await res.json()) as PromotionRow[]);
    } catch {
      setError(t('backoffice.promotions_load_error'));
    } finally {
      setLoading(false);
    }
  }, [globalAuthorizedFetch, t]);

  useEffect(() => {
    void loadPromotions();
  }, [loadPromotions]);

  const filtersRef = useRef({
    q,
    phone,
    country,
    city,
    usePurchaseWindow,
    purchaseFrom,
    purchaseTo,
    minSpendUsd,
    maxSpendUsd,
    excludeIds,
    sort,
  });
  filtersRef.current = {
    q,
    phone,
    country,
    city,
    usePurchaseWindow,
    purchaseFrom,
    purchaseTo,
    minSpendUsd,
    maxSpendUsd,
    excludeIds,
    sort,
  };

  const buildSegmentQsForPage = useCallback((pageNum: number) => {
    const f = filtersRef.current;
    const qs = new URLSearchParams();
    if (f.q.trim()) qs.set('q', f.q.trim());
    if (f.phone.trim()) qs.set('phone', f.phone.trim());
    if (f.country.trim()) qs.set('country', f.country.trim());
    if (f.city.trim()) qs.set('city', f.city.trim());
    if (f.usePurchaseWindow) {
      if (f.purchaseFrom) qs.set('purchaseFrom', new Date(f.purchaseFrom).toISOString());
      if (f.purchaseTo) qs.set('purchaseTo', new Date(f.purchaseTo).toISOString());
    }
    if (f.minSpendUsd.trim()) qs.set('minSpendUsd', f.minSpendUsd.trim());
    if (f.maxSpendUsd.trim()) qs.set('maxSpendUsd', f.maxSpendUsd.trim());
    const ex = f.excludeIds
      .split(/[\n,;]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (ex.length) qs.set('excludeIds', ex.join(','));
    qs.set('sort', f.sort);
    qs.set('page', String(pageNum));
    qs.set('pageSize', '25');
    return qs.toString();
  }, []);

  useEffect(() => {
    if (!segmentSearched) {
      setSegLoading(false);
      return;
    }
    let cancelled = false;
    setSegLoading(true);
    setSegError(null);
    void (async () => {
      try {
        const res = await globalAuthorizedFetch(
          `${API_BASE}/promotions/targeting/customers?${buildSegmentQsForPage(page)}`,
        );
        if (!res.ok) {
          const j = await res.json().catch(() => ({}));
          throw new Error((j as { message?: string }).message || 'segment failed');
        }
        const json = (await res.json()) as SegmentResponse;
        if (!cancelled) setSegment(json);
      } catch (e) {
        if (!cancelled) {
          setSegment(null);
          setSegError(e instanceof Error ? e.message : 'Failed');
        }
      } finally {
        if (!cancelled) setSegLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [buildSegmentQsForPage, globalAuthorizedFetch, page, searchNonce, segmentSearched]);

  const resetSegmentFilters = () => {
    setQ('');
    setPhone('');
    setCountry('');
    setCity('');
    setPurchaseFrom(defaultPurchaseFrom());
    setPurchaseTo(defaultPurchaseTo());
    setUsePurchaseWindow(true);
    setMinSpendUsd('');
    setMaxSpendUsd('');
    setExcludeIds('');
    setSort('period_spend_desc');
    setPage(1);
    setSegError(null);
    setSegment(null);
    setSegmentSearched(false);
    setSearchNonce((n) => n + 1);
  };

  const applySegmentFilters = () => {
    setSegmentSearched(true);
    setPage(1);
    setSearchNonce((n) => n + 1);
  };

  const resetForm = () => {
    setCode('');
    setDescription('');
    setDiscountType('PERCENT');
    setPercentOff('10');
    setFixedDiscountUsd('5');
    setRestrictedCustomerEmail('');
    setMinLifetimeSpendUsd('');
    setMinOrderSubtotalUsd('');
    setMaxRedemptionsTotal('');
    setMaxRedemptionsPerCustomer('');
    setExpiresAt('');
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const body: Record<string, unknown> = {
        code: code.trim(),
        description: description.trim() || undefined,
        discountType,
        isActive: true,
      };
      if (discountType === 'PERCENT') {
        body.percentOff = Number(percentOff);
      } else {
        body.fixedDiscountUsd = Number(fixedDiscountUsd);
      }
      if (restrictedCustomerEmail.trim()) {
        body.restrictedCustomerEmail = restrictedCustomerEmail.trim().toLowerCase();
      }
      if (minLifetimeSpendUsd.trim()) {
        body.minLifetimeSpendUsd = Number(minLifetimeSpendUsd);
      }
      if (minOrderSubtotalUsd.trim()) {
        body.minOrderSubtotalUsd = Number(minOrderSubtotalUsd);
      }
      if (maxRedemptionsTotal.trim()) {
        body.maxRedemptionsTotal = Number(maxRedemptionsTotal);
      }
      if (maxRedemptionsPerCustomer.trim()) {
        body.maxRedemptionsPerCustomer = Number(maxRedemptionsPerCustomer);
      }
      if (expiresAt.trim()) {
        body.expiresAt = new Date(expiresAt).toISOString();
      }

      const res = await globalAuthorizedFetch(`${API_BASE}/promotions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error((j as { message?: string }).message || 'err');
      }
      resetForm();
      setFormOpen(false);
      await loadPromotions();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('backoffice.promotions_save_error'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (id: string) => {
    if (!window.confirm(t('common.delete'))) return;
    try {
      const res = await globalAuthorizedFetch(`${API_BASE}/promotions/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('x');
      await loadPromotions();
    } catch {
      setError(t('backoffice.promotions_deactivate_error'));
    }
  };

  const valueCell = (r: PromotionRow) => {
    if (r.discountType === 'PERCENT') {
      return `${Number(r.percentOff ?? 0)}%`;
    }
    return `USD ${formatPriceFrom(Number(r.fixedDiscountUsd ?? 0), 'USD')}`;
  };

  const copyText = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      /* ignore */
    }
  };

  const applyCustomerAsRestricted = (email: string) => {
    setRestrictedCustomerEmail(email);
    setFormOpen(true);
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const filterSummary = useMemo(() => {
    const parts: string[] = [];
    if (q.trim()) parts.push(`“${q.trim()}”`);
    if (usePurchaseWindow) parts.push(t('backoffice.promo_seg_window_on', { defaultValue: 'Purchase window' }));
    else parts.push(t('backoffice.promo_seg_window_off', { defaultValue: 'All customers (directory)' }));
    if (minSpendUsd.trim()) parts.push(`≥ ${minSpendUsd} USD`);
    if (maxSpendUsd.trim()) parts.push(`≤ ${maxSpendUsd} USD`);
    if (excludeIds.trim()) parts.push(t('backoffice.promo_seg_exclusions', { defaultValue: 'Exclusions' }));
    return parts.join(' · ');
  }, [q, usePurchaseWindow, minSpendUsd, maxSpendUsd, excludeIds, t]);

  const totalPages = segment ? Math.max(1, Math.ceil(segment.total / segment.pageSize)) : 1;

  return (
    <MainLayout withSidebar sidebarItems={getBackofficeSidebarItems(t)} activePath="/workspace/promotions">
      <main className="workspace-page">
        <section className="relative overflow-hidden rounded-2xl border border-slate-200/90 bg-gradient-to-br from-amber-50/40 via-white to-brand-50/30 p-5 shadow-md ring-1 ring-slate-900/[0.04] sm:p-6">
          <div
            className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-amber-300/20 blur-3xl"
            aria-hidden
          />
          <div className="relative flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-4">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-500 to-orange-500 text-white shadow-lg">
                <Tag className="h-7 w-7" aria-hidden />
              </div>
              <div>
                <h1 className="workspace-page-title">
                  {t('backoffice.promotions_title')}
                </h1>
                <p className="mt-1 max-w-3xl text-sm leading-relaxed text-slate-600 sm:text-base">
                  {t(
                    'backoffice.promotions_subtitle_rich',
                    'Find customers by name, email, or phone, then narrow by purchase dates and spend. Stack filters (search + window + min/max + exclusions) and run search. Create single-use codes restricted to one customer when needed.',
                  )}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 rounded-xl border border-amber-200/60 bg-white/90 px-4 py-3 shadow-sm">
              <Users className="h-5 w-5 text-amber-600" aria-hidden />
              <div>
                <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500">
                  {t('backoffice.promo_seg_codes_active', { defaultValue: 'Codes' })}
                </p>
                <p className="text-lg font-bold text-slate-900">{rows.filter((r) => r.isActive).length}</p>
              </div>
            </div>
          </div>
        </section>

        {error ? (
          <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</div>
        ) : null}

        {/* Customer targeting */}
        <section className="workspace-panel space-y-3">
          <div className="border-b border-slate-100 pb-4">
            <button
              type="button"
              className="flex w-full items-start gap-3 rounded-xl p-1 text-left transition hover:bg-slate-50/80 sm:items-center"
              onClick={() => setTargetingExpanded((v) => !v)}
              aria-expanded={targetingExpanded}
            >
              <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand-100 text-brand-700 sm:mt-0">
                <Filter className="h-5 w-5" aria-hidden />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="text-base font-semibold text-slate-900">
                    {t('backoffice.promo_seg_title', { defaultValue: 'Customer targeting' })}
                  </h2>
                  <ChevronDown
                    className={`h-5 w-5 shrink-0 text-slate-500 transition-transform ${targetingExpanded ? 'rotate-180' : ''}`}
                    aria-hidden
                  />
                </div>
                <p className="mt-0.5 text-xs text-slate-600">
                  {targetingExpanded
                    ? filterSummary
                    : t('backoffice.promo_seg_collapsed_sub', {
                        defaultValue:
                          'Expand to set filters and search. You can still create codes restricted by customer email without searching.',
                      })}
                </p>
              </div>
            </button>
          </div>

          {targetingExpanded ? (
            <>
              <div className="flex flex-wrap justify-end gap-3">
                <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-medium text-slate-700">
                  <input
                    type="checkbox"
                    checked={usePurchaseWindow}
                    onChange={(e) => setUsePurchaseWindow(e.target.checked)}
                    className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                  />
                  {t('backoffice.promo_seg_use_window', { defaultValue: 'Filter by purchase period' })}
                </label>
              </div>

              <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
            <div className="space-y-4 lg:col-span-5">
              <div>
                <label className={labelClass}>
                  {t('backoffice.promo_seg_search', { defaultValue: 'Search (name, email, phone)' })}
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden />
                  <input
                    className={`${inputClass} pl-10`}
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder={t('backoffice.promo_seg_search_ph', { defaultValue: 'e.g. Marie, @gmail, +237…' })}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label className={labelClass}>{t('backoffice.promo_seg_phone', { defaultValue: 'Phone' })}</label>
                  <input className={inputClass} value={phone} onChange={(e) => setPhone(e.target.value)} />
                </div>
                <div>
                  <label className={labelClass}>{t('backoffice.promo_seg_city', { defaultValue: 'City' })}</label>
                  <input className={inputClass} value={city} onChange={(e) => setCity(e.target.value)} />
                </div>
                <div className="sm:col-span-2">
                  <label className={labelClass}>
                    {t('backoffice.promo_seg_country', { defaultValue: 'Country' })}
                  </label>
                  <input className={inputClass} value={country} onChange={(e) => setCountry(e.target.value)} />
                </div>
              </div>
            </div>

            <div
              className={[
                'space-y-4 rounded-2xl border p-4 lg:col-span-4',
                usePurchaseWindow
                  ? 'border-brand-200/80 bg-brand-50/30'
                  : 'border-dashed border-slate-200 bg-slate-50/40 opacity-70',
              ].join(' ')}
            >
              <p className="text-xs font-bold uppercase tracking-wide text-slate-600">
                {t('backoffice.promo_seg_period', { defaultValue: 'Purchase window' })}
              </p>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label className={labelClass}>{t('backoffice.promo_seg_from', { defaultValue: 'From' })}</label>
                  <input
                    className={inputClass}
                    type="datetime-local"
                    value={purchaseFrom}
                    onChange={(e) => setPurchaseFrom(e.target.value)}
                    disabled={!usePurchaseWindow}
                  />
                </div>
                <div>
                  <label className={labelClass}>{t('backoffice.promo_seg_to', { defaultValue: 'To' })}</label>
                  <input
                    className={inputClass}
                    type="datetime-local"
                    value={purchaseTo}
                    onChange={(e) => setPurchaseTo(e.target.value)}
                    disabled={!usePurchaseWindow}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label className={labelClass}>
                    {t('backoffice.promo_seg_min_spend', { defaultValue: 'Min spend in window (USD)' })}
                  </label>
                  <input
                    className={inputClass}
                    type="number"
                    min={0}
                    step="0.01"
                    value={minSpendUsd}
                    onChange={(e) => setMinSpendUsd(e.target.value)}
                    disabled={!usePurchaseWindow}
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className={labelClass}>
                    {t('backoffice.promo_seg_max_spend', { defaultValue: 'Max spend in window (USD)' })}
                  </label>
                  <input
                    className={inputClass}
                    type="number"
                    min={0}
                    step="0.01"
                    value={maxSpendUsd}
                    onChange={(e) => setMaxSpendUsd(e.target.value)}
                    disabled={!usePurchaseWindow}
                    placeholder={t('backoffice.promo_seg_optional', { defaultValue: 'Optional' })}
                  />
                </div>
              </div>
              <p className="text-[11px] leading-relaxed text-slate-600">
                {t(
                  'backoffice.promo_seg_period_hint',
                  'Requires at least one paid order in the window. Min/max apply to total spend in that window only.',
                )}
              </p>
            </div>

            <div className="space-y-4 lg:col-span-3">
              <div>
                <label className={labelClass}>
                  {t('backoffice.promo_seg_exclude', { defaultValue: 'Exclude customer IDs' })}
                </label>
                <textarea
                  className={`${inputClass} min-h-[7rem] resize-y py-2.5 font-mono text-xs`}
                  value={excludeIds}
                  onChange={(e) => setExcludeIds(e.target.value)}
                  placeholder={t('backoffice.promo_seg_exclude_ph', {
                    defaultValue: 'Comma or newline separated…',
                  })}
                />
                <p className="mt-1 text-[11px] text-slate-500">
                  {t('backoffice.promo_seg_exclude_hint', { defaultValue: 'Deductive filter: these users never appear.' })}
                </p>
              </div>
              <div>
                <label className={labelClass}>{t('backoffice.promo_seg_sort', { defaultValue: 'Sort' })}</label>
                <select className={inputClass} value={sort} onChange={(e) => setSort(e.target.value)}>
                  <option value="period_spend_desc">
                    {t('backoffice.promo_seg_sort_period_desc', { defaultValue: 'Spend in window (high → low)' })}
                  </option>
                  <option value="period_spend_asc">
                    {t('backoffice.promo_seg_sort_period_asc', { defaultValue: 'Spend in window (low → high)' })}
                  </option>
                  <option value="lifetime_spend_desc">
                    {t('backoffice.promo_seg_sort_life_desc', { defaultValue: 'Lifetime spend (high → low)' })}
                  </option>
                  <option value="name_asc">
                    {t('backoffice.promo_seg_sort_name', { defaultValue: 'Name A–Z' })}
                  </option>
                  <option value="registered_desc">
                    {t('backoffice.promo_seg_sort_registered', { defaultValue: 'Newest customers' })}
                  </option>
                </select>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 border-t border-slate-100 pt-4">
            <button type="button" className={btnPrimary} onClick={applySegmentFilters} disabled={segLoading}>
              {segLoading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                  {t('common.loading')}
                </>
              ) : (
                <>
                  <Search className="h-4 w-4" aria-hidden />
                  {t('backoffice.promo_seg_run', { defaultValue: 'Run search' })}
                </>
              )}
            </button>
            <button
              type="button"
              className={btnSecondary}
              onClick={() => resetSegmentFilters()}
              disabled={segLoading}
            >
              {t('backoffice.promo_seg_reset', { defaultValue: 'Clear filters' })}
            </button>
            {segment ? (
              <span className="text-sm text-slate-600">
                <strong className="text-slate-900">{segment.total}</strong>{' '}
                {t('backoffice.promo_seg_results', { defaultValue: 'matches' })}
                {segment.mode === 'period' ? (
                  <span className="ml-2 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-800">
                    {t('backoffice.promo_seg_mode_period', { defaultValue: 'Period mode' })}
                  </span>
                ) : (
                  <span className="ml-2 rounded-full bg-slate-200/80 px-2 py-0.5 text-xs font-semibold text-slate-700">
                    {t('backoffice.promo_seg_mode_dir', { defaultValue: 'Directory' })}
                  </span>
                )}
              </span>
            ) : null}
          </div>
            </>
          ) : null}

          {segError ? (
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              {segError}
            </div>
          ) : null}

          <div className="overflow-x-auto rounded-xl border border-slate-200/90 bg-white shadow-sm ring-1 ring-slate-900/[0.03]">
            <table className="min-w-[56rem] w-full text-left text-sm">
              <thead className="border-b border-slate-200 bg-slate-100/90 text-xs font-semibold uppercase tracking-wide text-slate-600">
                <tr>
                  <th className="px-3 py-3">{t('backoffice.promo_seg_col_customer', { defaultValue: 'Customer' })}</th>
                  <th className="px-3 py-3">{t('backoffice.promo_seg_col_contact', { defaultValue: 'Contact' })}</th>
                  <th className="px-3 py-3">{t('backoffice.promo_seg_col_location', { defaultValue: 'Location' })}</th>
                  <th className="px-3 py-3 text-right">
                    {t('backoffice.promo_seg_col_period', { defaultValue: 'In window' })}
                  </th>
                  <th className="px-3 py-3 text-right">
                    {t('backoffice.promo_seg_col_lifetime', { defaultValue: 'Lifetime' })}
                  </th>
                  <th className="px-3 py-3">{t('backoffice.promo_seg_col_actions', { defaultValue: 'Actions' })}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {!segmentSearched ? (
                  <tr>
                    <td colSpan={6} className="px-3 py-10 text-center text-sm text-slate-500">
                      {t('backoffice.promo_seg_table_idle', {
                        defaultValue: 'Expand filters above and click Run search to load customers.',
                      })}
                    </td>
                  </tr>
                ) : segLoading && !segment ? (
                  <tr>
                    <td colSpan={6} className="px-3 py-10 text-center text-slate-500">
                      <Loader2 className="mx-auto h-6 w-6 animate-spin text-brand-600" aria-hidden />
                    </td>
                  </tr>
                ) : segment && segment.items.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-3 py-10 text-center text-slate-500">
                      {t('backoffice.promo_seg_empty', { defaultValue: 'No customers match. Loosen filters or run without min spend.' })}
                    </td>
                  </tr>
                ) : (
                  segment?.items.map((c) => (
                    <tr key={c.id} className="hover:bg-brand-50/40">
                      <td className="px-3 py-3">
                        <p className="font-semibold text-slate-900">{c.fullName || '—'}</p>
                        <p className="font-mono text-[11px] text-slate-500">{c.id}</p>
                      </td>
                      <td className="px-3 py-3 text-slate-700">
                        <p>{c.email}</p>
                        <p className="text-xs text-slate-500">{c.phone || '—'}</p>
                      </td>
                      <td className="px-3 py-3 text-xs text-slate-600">
                        {[c.city, c.country].filter(Boolean).join(', ') || '—'}
                      </td>
                      <td className="px-3 py-3 text-right tabular-nums">
                        {c.periodSpendUsd != null ? (
                          <>
                            <span className="font-bold text-slate-900">
                              USD {formatPriceFrom(c.periodSpendUsd, 'USD')}
                            </span>
                            <p className="text-xs text-slate-500">
                              {c.periodOrderCount} {t('backoffice.promo_seg_orders', { defaultValue: 'orders' })}
                            </p>
                          </>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                      <td className="px-3 py-3 text-right tabular-nums">
                        <span className="font-semibold text-slate-800">
                          USD {formatPriceFrom(c.lifetimeSpendUsd, 'USD')}
                        </span>
                        <p className="text-xs text-slate-500">{c.lifetimeOrderCount} orders</p>
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex flex-col gap-1 sm:flex-row sm:flex-wrap">
                          <button type="button" className={btnGhost} onClick={() => void copyText(c.id)}>
                            <Copy className="h-3.5 w-3.5" aria-hidden />
                            ID
                          </button>
                          <button type="button" className={btnGhost} onClick={() => applyCustomerAsRestricted(c.email)}>
                            <UserPlus className="h-3.5 w-3.5" aria-hidden />
                            {t('backoffice.promo_seg_use_restricted', { defaultValue: 'Restrict code' })}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {segmentSearched && segment && segment.total > segment.pageSize ? (
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-slate-600">
                {t('backoffice.promo_seg_page', {
                  defaultValue: 'Page {{current}} of {{total}}',
                  current: page,
                  total: totalPages,
                })}
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  className={btnSecondary}
                  disabled={page <= 1 || segLoading}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  <ChevronLeft className="h-4 w-4" aria-hidden />
                </button>
                <button
                  type="button"
                  className={btnSecondary}
                  disabled={page >= totalPages || segLoading}
                  onClick={() => setPage((p) => p + 1)}
                >
                  <ChevronRight className="h-4 w-4" aria-hidden />
                </button>
              </div>
            </div>
          ) : null}
        </section>

        {/* Codes */}
        <section ref={formRef} className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-brand-600" aria-hidden />
              <h2 className="text-base font-semibold text-slate-900">
                {t('backoffice.promo_codes_heading', { defaultValue: 'Promotion codes' })}
              </h2>
            </div>
            <button
              type="button"
              onClick={() => {
                setFormOpen((v) => !v);
                setError(null);
              }}
              className={`${btnPrimary} !min-h-10`}
            >
              <Tag className="h-4 w-4" aria-hidden />
              {formOpen ? t('common.cancel') : t('backoffice.promotions_create_open')}
            </button>
          </div>

          {formOpen ? (
            <form
              onSubmit={(e) => void handleCreate(e)}
              className="glass-panel max-w-3xl space-y-4 p-5 sm:p-6"
            >
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <label className={labelClass}>{t('backoffice.promotions_form_code')}</label>
                  <input
                    className={`${inputClass} font-mono uppercase`}
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    required
                    autoComplete="off"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className={labelClass}>{t('backoffice.promotions_form_description')}</label>
                  <input className={inputClass} value={description} onChange={(e) => setDescription(e.target.value)} />
                </div>
                <div>
                  <label className={labelClass}>{t('backoffice.promotions_form_discount_type')}</label>
                  <select
                    className={inputClass}
                    value={discountType}
                    onChange={(e) => setDiscountType(e.target.value as 'PERCENT' | 'FIXED_USD')}
                  >
                    <option value="PERCENT">{t('backoffice.promotions_type_percent')}</option>
                    <option value="FIXED_USD">{t('backoffice.promotions_type_fixed')}</option>
                  </select>
                </div>
                {discountType === 'PERCENT' ? (
                  <div>
                    <label className={labelClass}>{t('backoffice.promotions_form_percent')}</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0.01"
                      max="100"
                      className={inputClass}
                      value={percentOff}
                      onChange={(e) => setPercentOff(e.target.value)}
                      required
                    />
                  </div>
                ) : (
                  <div>
                    <label className={labelClass}>{t('backoffice.promotions_form_fixed')}</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0.01"
                      className={inputClass}
                      value={fixedDiscountUsd}
                      onChange={(e) => setFixedDiscountUsd(e.target.value)}
                      required
                    />
                  </div>
                )}
                <div className="sm:col-span-2 rounded-xl border border-brand-200/80 bg-brand-50/40 p-4">
                  <label className={labelClass}>
                    {t('backoffice.promotions_form_restricted_customer')}
                  </label>
                  <input
                    type="email"
                    autoComplete="off"
                    className={inputClass}
                    value={restrictedCustomerEmail}
                    onChange={(e) => setRestrictedCustomerEmail(e.target.value)}
                    placeholder={t('backoffice.promotions_form_restricted_ph', {
                      defaultValue: 'customer@example.com',
                    })}
                  />
                  <p className="mt-1 text-xs text-slate-600">{t('backoffice.promotions_form_restricted_hint')}</p>
                </div>
                <div>
                  <label className={labelClass}>{t('backoffice.promotions_form_min_lifetime')}</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    className={inputClass}
                    value={minLifetimeSpendUsd}
                    onChange={(e) => setMinLifetimeSpendUsd(e.target.value)}
                  />
                </div>
                <div>
                  <label className={labelClass}>{t('backoffice.promotions_form_min_subtotal')}</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    className={inputClass}
                    value={minOrderSubtotalUsd}
                    onChange={(e) => setMinOrderSubtotalUsd(e.target.value)}
                  />
                </div>
                <div>
                  <label className={labelClass}>{t('backoffice.promotions_form_max_total')}</label>
                  <input
                    type="number"
                    min="1"
                    className={inputClass}
                    value={maxRedemptionsTotal}
                    onChange={(e) => setMaxRedemptionsTotal(e.target.value)}
                  />
                </div>
                <div>
                  <label className={labelClass}>{t('backoffice.promotions_form_max_per_customer')}</label>
                  <input
                    type="number"
                    min="1"
                    className={inputClass}
                    value={maxRedemptionsPerCustomer}
                    onChange={(e) => setMaxRedemptionsPerCustomer(e.target.value)}
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className={labelClass}>{t('backoffice.promotions_form_expires')}</label>
                  <input
                    type="datetime-local"
                    className={`${inputClass} max-w-xs`}
                    value={expiresAt}
                    onChange={(e) => setExpiresAt(e.target.value)}
                  />
                </div>
              </div>
              <div className="flex flex-wrap gap-3">
                <button type="submit" disabled={saving} className={btnPrimary}>
                  {saving ? t('common.saving') : t('backoffice.promotions_save')}
                </button>
                <button type="button" className={btnSecondary} onClick={() => setFormOpen(false)}>
                  {t('common.cancel')}
                </button>
              </div>
            </form>
          ) : null}

          <div className="overflow-x-auto rounded-2xl border border-slate-200/90 bg-white shadow-sm ring-1 ring-slate-900/[0.04]">
            <table className="min-w-full text-left text-sm">
              <thead className="border-b border-slate-200 bg-slate-100/90 text-xs font-semibold uppercase tracking-wide text-slate-600">
                <tr>
                  <th className="px-4 py-3">{t('backoffice.promotions_col_code')}</th>
                  <th className="px-4 py-3">{t('backoffice.promotions_col_type')}</th>
                  <th className="px-4 py-3">{t('backoffice.promotions_col_value')}</th>
                  <th className="px-4 py-3">{t('backoffice.promotions_col_restricted')}</th>
                  <th className="px-4 py-3">{t('backoffice.promotions_col_expires')}</th>
                  <th className="px-4 py-3">{t('backoffice.promotions_col_redemptions', { defaultValue: 'Uses' })}</th>
                  <th className="px-4 py-3">{t('backoffice.promotions_col_active')}</th>
                  <th className="px-4 py-3">{t('backoffice.promotions_col_actions')}</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <PaginatedListLoading tableColSpan={8} title={t('common.list_loading_title')} />
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-slate-500">
                      {t('backoffice.promotions_empty')}
                    </td>
                  </tr>
                ) : (
                  rows.map((r) => (
                    <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50/80">
                      <td className="px-4 py-3 font-mono text-xs font-semibold text-slate-900">{r.code}</td>
                      <td className="px-4 py-3">
                        {r.discountType === 'PERCENT'
                          ? t('backoffice.promotions_type_percent')
                          : t('backoffice.promotions_type_fixed')}
                      </td>
                      <td className="px-4 py-3 tabular-nums">{valueCell(r)}</td>
                      <td className="px-4 py-3 text-xs text-slate-600">
                        {r.restrictedCustomer ? r.restrictedCustomer.email : t('backoffice.promotions_unlimited')}
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-600">
                        {r.expiresAt ? new Date(r.expiresAt).toLocaleString() : t('backoffice.promotions_unlimited')}
                      </td>
                      <td className="px-4 py-3 font-semibold tabular-nums text-slate-800">{r._count?.orders ?? 0}</td>
                      <td className="px-4 py-3">{r.isActive ? '✓' : '—'}</td>
                      <td className="px-4 py-3">
                        {r.isActive ? (
                          <button
                            type="button"
                            onClick={() => void handleDeactivate(r.id)}
                            className="text-xs font-semibold text-rose-700 hover:underline"
                          >
                            {t('backoffice.promotions_deactivate')}
                          </button>
                        ) : (
                          '—'
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      </main>
    </MainLayout>
  );
}
